import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";
import { useSection, useSettings, resolveUrl } from "../lib/useCMS";
import { PageShell } from "../components/PageShell";
import { PageHero } from "../components/page/PageHero";
import { SEO } from "../components/page/SEO";
import { JsonLd } from "../components/page/JsonLd";
import { SITE_URL, absoluteUrl } from "../lib/site";

const CREDENTIALS = [
  "Former Professional Defenseman",
  "Certified Personal Trainer",
  "Certified Nutrition Coach",
  "Masters Degree",
  "USA Hockey Certified Coach",
  "Published Hockey Development Author",
  "20 Year Military Veteran",
];

export default function CoachPage() {
  const p = useSection("page_coach", {});
  const settings = useSettings({ square_booking_url: "" });
  const ctaUrl = p.cta_url || settings.square_booking_url;

  return (
    <PageShell>
      <SEO title={p.meta_title} description={p.meta_description} canonical="/coach" image={p.hero_image_url} />
      <JsonLd
        id="person-coach-mike"
        data={{
          "@context": "https://schema.org",
          "@type": "Person",
          "@id": `${SITE_URL}/coach#mike-fournier`,
          name: "Mike Fournier",
          givenName: "Mike",
          familyName: "Fournier",
          jobTitle: "Founder & Head Coach, Built Different Hockey",
          image: p.hero_image_url ? absoluteUrl(p.hero_image_url) : undefined,
          url: `${SITE_URL}/coach`,
          worksFor: { "@id": `${SITE_URL}/#organization` },
          description: p.meta_description,
        }}
      />
      <PageHero overline={p.hero_overline} heading={p.hero_heading} image={p.hero_image_url} />

      <section className="bg-[#0a0a0a] py-20 md:py-28 border-b border-white/5">
        <div className="mx-auto max-w-[1100px] px-6 md:px-10 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-x-0 gap-y-10 md:gap-10 items-start">
          <div className="md:col-span-5">
            <div className="aspect-[4/5] overflow-hidden bento-card relative">
              {p.hero_image_url && <img src={resolveUrl(p.hero_image_url)} alt="Coach Mike Fournier, founder of Built Different Hockey" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover grayscale contrast-125" />}
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <div className="font-display text-3xl md:text-4xl uppercase leading-[0.95]">Coach<br /><span className="text-brand-light">Mike</span><br />Fournier</div>
              </div>
            </div>
          </div>
          <div className="md:col-span-7">
            <p className="text-lg md:text-xl text-zinc-300 leading-relaxed whitespace-pre-line">{p.intro_body}</p>
            <div className="mt-10">
              <div className="text-[10px] tracking-[0.3em] uppercase text-brand-light font-bold mb-5">The Credentials</div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                {CREDENTIALS.map((c, i) => (
                  <motion.li key={c} initial={{ opacity: 0, x: -8 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.06 }} className="flex items-center gap-3 border-b border-white/10 pb-3">
                    <CheckCircle2 className="h-4 w-4 text-brand-light shrink-0" />
                    <span className="text-sm md:text-base text-zinc-200">{c}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#0f0f0f] py-20 md:py-28 border-b border-white/5">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16 grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { t: p.section_2_title, b: p.section_2_body },
            { t: p.section_3_title, b: p.section_3_body },
            { t: p.section_4_title, b: p.section_4_body },
          ].filter((x) => x.t || x.b).map((s, i) => (
            <div key={i} className="bento-card p-7 md:p-9">
              <div className="font-display text-5xl text-brand-light">{String(i + 1).padStart(2, "0")}</div>
              <h3 className="font-display uppercase text-2xl mt-3 leading-tight">{s.t}</h3>
              <p className="text-zinc-400 mt-3 leading-relaxed">{s.b}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#0a0a0a] py-20">
        <div className="mx-auto max-w-[1100px] px-6 md:px-10 lg:px-16 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <p className="overline">Train</p>
            <h2 className="font-display text-3xl md:text-4xl uppercase mt-3 leading-tight">Work with Coach Mike.</h2>
          </div>
          {ctaUrl && (
            <a href={ctaUrl} target="_blank" rel="noopener noreferrer" className="btn-primary">
              {p.cta_label || "Book a Clinic"} <ArrowUpRight className="h-5 w-5" />
            </a>
          )}
        </div>
      </section>
    </PageShell>
  );
}
